<template>
  <div class="login-page">
    <div class="login-card">
      <div class="brand-section">
        <div class="brand-badge">B</div>
        <h1>BEZ 后台登录</h1>
        <p class="muted">策略池与净值分发系统</p>
      </div>

      <form @submit.prevent="handleLogin" class="login-form">
        <div class="field">
          <label>邮箱</label>
          <input
            v-model="email"
            type="email"
            class="input"
            placeholder="your@email.com"
            required
          />
        </div>

        <div class="field">
          <label>密码</label>
          <input
            v-model="password"
            type="password"
            class="input"
            placeholder="••••••••"
            required
          />
        </div>

        <div class="remember-field">
          <label class="checkbox-label">
            <input
              v-model="rememberMe"
              type="checkbox"
              class="checkbox"
            />
            <span class="checkbox-text">记住密码</span>
          </label>
        </div>

        <button type="submit" class="btn btn-primary" :disabled="loading">
          {{ loading ? '登录中...' : '登录' }}
        </button>

        <div v-if="error" class="error">{{ error }}</div>
      </form>
    </div>
  </div>
</template>

<script>
import api from '../api'

const REMEMBER_KEY = 'rememberLoginCredentials'

export default {
  name: 'LoginPage',
  data() {
    return {
      email: '',
      password: '',
      rememberMe: true,
      loading: false,
      error: ''
    }
  },
  async mounted() {
    // 检查是否已登录
    if (api.isAuthenticated()) {
      const { userInfo } = api.getAuthInfo()
      if (userInfo) {
        console.log('✅ 检测到已登录:', userInfo.name)

        // 根据角色跳转
        if (userInfo.role === 'ADMIN') {
          this.$router.push('/admin')
        } else {
          this.$router.push('/dashboard')
        }
        return
      }
    }

    // 加载记住的密码
    this.loadRememberedCredentials()
  },
  methods: {
    // 加载保存的凭据
    loadRememberedCredentials() {
      try {
        const saved = localStorage.getItem(REMEMBER_KEY)
        if (saved) {
          const credentials = JSON.parse(saved)
          this.email = credentials.email || ''
          this.password = credentials.password || ''
          this.rememberMe = true
          console.log('📝 已加载保存的登录凭据')
        }
      } catch (e) {
        console.error('加载保存的凭据失败:', e)
        localStorage.removeItem(REMEMBER_KEY)
      }
    },

    // 保存凭据
    saveCredentials() {
      if (this.rememberMe) {
        const credentials = {
          email: this.email,
          password: this.password
        }
        localStorage.setItem(REMEMBER_KEY, JSON.stringify(credentials))
        console.log('💾 已保存登录凭据')
      } else {
        localStorage.removeItem(REMEMBER_KEY)
        console.log('🗑️ 已清除保存的登录凭据')
      }
    },

    async handleLogin() {
      this.error = ''
      this.loading = true

      try {
        // 调用后端登录 API
        console.log('🚀 正在登录...', this.email)
        const response = await api.login(this.email, this.password)

        if (response.success) {
          const { token, email, name, role, clientId } = response.data

          console.log('✅ 登录成功:', name, role)

          // 登录成功后保存/清除凭据
          this.saveCredentials()

          // 保存登录信息
          api.saveAuthInfo(token, {
            email,
            name,
            role,
            clientId
          })

          // 根据角色跳转
          if (role === 'ADMIN') {
            this.$router.push('/admin')
          } else {
            this.$router.push('/dashboard')
          }
        } else {
          this.error = response.message || '登录失败'
        }
      } catch (err) {
        console.error('❌ 登录失败:', err)
        this.error = err.response?.data?.message || err.message || '登录失败，请检查邮箱和密码'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: var(--bg);
}

.login-card {
  width: 100%;
  max-width: 420px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 40px 32px;
}

.brand-section {
  text-align: center;
  margin-bottom: 32px;
}

.brand-badge {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: inline-grid;
  place-items: center;
  background: #222b36;
  color: #fff;
  font-weight: 700;
  font-size: 24px;
  border: 1px solid var(--border);
  margin-bottom: 16px;
}

h1 {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px;
  color: var(--text);
}

.muted {
  color: var(--muted);
  font-size: 14px;
  margin: 0;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  color: var(--muted);
  font-size: 13px;
  font-weight: 500;
}

.input {
  width: 100%;
  height: 48px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--input);
  color: var(--text);
  padding: 0 14px;
  font-size: 15px;
  transition: border-color 0.2s;
}

.input:focus {
  outline: none;
  border-color: var(--primary);
}

.btn {
  height: 48px;
  border-radius: 10px;
  border: none;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: var(--primary-press);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error {
  margin-top: -8px;
  padding: 12px 14px;
  background: rgba(255, 107, 107, 0.08);
  border: 1px solid rgba(255, 107, 107, 0.35);
  border-radius: 8px;
  color: #ffb3b3;
  font-size: 13px;
}

.remember-field {
  margin-top: -8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.checkbox {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid var(--border);
  background: var(--input);
  cursor: pointer;
  accent-color: var(--primary);
}

.checkbox:checked {
  background: var(--primary);
  border-color: var(--primary);
}

.checkbox-text {
  color: var(--muted);
  font-size: 14px;
}
</style>

